import { Button } from '@/components/ui/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/shadcn/dialog';
import { useTranslations } from '@/hooks/useTranslations';
import { router } from '@inertiajs/react';
import { ArrowLeft, BookOpenText, Building2, Contact, FolderKanban, Loader2, Magnet, Search, SquareCheckBig } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

type SearchResultType = 'company' | 'person' | 'lead' | 'project' | 'task' | 'article';

interface SearchResult {
    type: SearchResultType;
    id: string;
    title: string;
    subtitle: string;
    url: string;
}

interface RawSearchResult {
    type?: unknown;
    id?: unknown;
    title?: unknown;
    subtitle?: unknown;
    url?: unknown;
}

interface GroupedResults {
    type: SearchResultType;
    label: string;
    icon: React.ReactNode;
    items: SearchResult[];
}

interface GlobalSearchProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const TYPE_META: Record<SearchResult['type'], { label: string; icon: React.ReactNode }> = {
    company: { label: 'Companies', icon: <Building2 className="size-3.5" /> },
    person: { label: 'Contacts', icon: <Contact className="size-3.5" /> },
    lead: { label: 'Leads', icon: <Magnet className="size-3.5" /> },
    project: { label: 'Projects', icon: <FolderKanban className="size-3.5" /> },
    task: { label: 'Tasks', icon: <SquareCheckBig className="size-3.5" /> },
    article: { label: 'Knowledge Base', icon: <BookOpenText className="size-3.5" /> },
};

const TYPE_ORDER: SearchResultType[] = ['company', 'person', 'lead', 'project', 'task', 'article'];

function isSearchResultType(value: unknown): value is SearchResultType {
    return typeof value === 'string' && TYPE_ORDER.includes(value as SearchResultType);
}

function stringifySearchValue(value: unknown, fallback = ''): string {
    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (value && typeof value === 'object') {
        const fields = value as Record<string, unknown>;

        return stringifySearchValue(fields.label ?? fields.name ?? fields.value, fallback);
    }

    return fallback;
}

function normalizeSearchResults(results: unknown, fallbackTitle: string): SearchResult[] {
    if (!Array.isArray(results)) {
        return [];
    }

    return results.flatMap((result): SearchResult[] => {
        if (!result || typeof result !== 'object') {
            return [];
        }

        const raw = result as RawSearchResult;

        if (!isSearchResultType(raw.type)) {
            return [];
        }

        const id = stringifySearchValue(raw.id);
        const url = stringifySearchValue(raw.url);

        if (!id || !url) {
            return [];
        }

        return [
            {
                type: raw.type,
                id,
                title: stringifySearchValue(raw.title, fallbackTitle),
                subtitle: stringifySearchValue(raw.subtitle),
                url,
            },
        ];
    });
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
    const { t } = useTranslations();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const flatResults = results;

    const grouped = TYPE_ORDER.reduce<GroupedResults[]>((acc, type) => {
        const items = flatResults.filter((r) => r.type === type);
        if (items.length > 0) {
            acc.push({ type, icon: TYPE_META[type].icon, label: t(`search.types.${type}`, TYPE_META[type].label), items });
        }
        return acc;
    }, []);

    const flatIndexed = grouped.flatMap((g) => g.items);

    const doSearch = useCallback(
        (q: string) => {
            if (abortRef.current) {
                abortRef.current.abort();
            }
            if (q.length < 2) {
                setResults([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            const ctrl = new AbortController();
            abortRef.current = ctrl;
            fetch(`/api/global-search?q=${encodeURIComponent(q)}`, {
                headers: { Accept: 'application/json' },
                signal: ctrl.signal,
            })
                .then((r) => r.json())
                .then((data) => {
                    setResults(normalizeSearchResults(data.results, t('search.untitled', 'Untitled')));
                    setActiveIndex(0);
                })
                .catch(() => {
                    if (abortRef.current === ctrl) {
                        setResults([]);
                    }
                })
                .finally(() => {
                    if (abortRef.current === ctrl) {
                        setLoading(false);
                    }
                });
        },
        [t],
    );

    const handleQueryChange = (value: string) => {
        setQuery(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(value), 250);
    };

    const navigate = (url: string) => {
        onOpenChange(false);
        router.visit(url);
    };

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setQuery('');
            setResults([]);
            setActiveIndex(0);
        }
    }, [open]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                onOpenChange(!open);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onOpenChange]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(0, Math.min(i + 1, flatIndexed.length - 1)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && flatIndexed[activeIndex]) {
            navigate(flatIndexed[activeIndex].url);
        }
    };

    const isEmpty = query.length >= 2 && !loading && results.length === 0;
    const bodyKey = loading ? 'loading' : isEmpty ? 'empty' : grouped.length > 0 ? `results-${grouped.length}` : 'hint';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                onKeyDown={handleKeyDown}
                className={`data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100 data-[state=closed]:slide-out-to-top-4 data-[state=open]:slide-in-from-top-4 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-top-0 sm:data-[state=open]:slide-in-from-top-0 top-0 left-0 flex h-[100dvh] max-h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 shadow-none duration-200 sm:top-[12%] sm:left-1/2 sm:h-auto sm:max-h-[min(34rem,72vh)] sm:w-full sm:max-w-2xl sm:-translate-x-1/2 sm:translate-y-0 sm:rounded-xl sm:border sm:shadow-lg`}
            >
                <DialogTitle className="sr-only">{t('search.dialog_title', 'Global search')}</DialogTitle>
                <DialogDescription className="sr-only">
                    {t('search.dialog_description', 'Search companies, contacts, leads, projects, and tasks.')}
                </DialogDescription>

                {/* Search input */}
                <div className="bg-background flex shrink-0 items-center gap-3 border-b px-4 py-4 sm:py-3">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onOpenChange(false)}
                        className="text-muted-foreground -ml-1 size-8 shrink-0 sm:hidden"
                        aria-label={t('search.footer.close', 'close')}
                    >
                        <ArrowLeft className="size-5" />
                    </Button>
                    <Search className="text-muted-foreground hidden size-4 shrink-0 sm:block" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => handleQueryChange(e.target.value)}
                        placeholder={t('search.placeholder', 'Search anything…')}
                        className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-base outline-none sm:text-sm"
                    />
                    {loading && <Loader2 className="text-muted-foreground size-4 shrink-0 animate-spin" />}
                    <kbd className="bg-muted text-muted-foreground hidden h-5 items-center gap-0.5 rounded border px-1.5 font-mono text-[10px] sm:inline-flex">
                        <span className="text-xs">⌘</span>K
                    </kbd>
                </div>

                {/* Results */}
                <div key={bodyKey} className="animate-in fade-in slide-in-from-top-1 min-h-0 flex-1 overflow-y-auto duration-150 sm:flex-none">
                    {grouped.length > 0 && (
                        <div className="py-2">
                            {grouped.map((group) => {
                                return (
                                    <div key={group.type}>
                                        <div className="text-muted-foreground flex items-center gap-1.5 px-4 py-1.5 text-[11px] font-medium tracking-wider uppercase">
                                            {group.icon}
                                            {group.label}
                                        </div>
                                        {group.items.map((item) => {
                                            const flatIdx = flatIndexed.indexOf(item);
                                            const isActive = flatIdx === activeIndex;
                                            return (
                                                <button
                                                    key={item.id}
                                                    className={`flex w-full cursor-pointer items-start gap-3 rounded-md px-4 py-3 text-left transition-colors sm:py-2 ${
                                                        isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60'
                                                    }`}
                                                    onMouseEnter={() => setActiveIndex(flatIdx)}
                                                    onClick={() => navigate(item.url)}
                                                >
                                                    <span className="text-muted-foreground mt-0.5 shrink-0">{TYPE_META[item.type].icon}</span>
                                                    <span className="min-w-0">
                                                        <span className="block truncate text-sm font-medium">{item.title}</span>
                                                        {item.subtitle && (
                                                            <span className="text-muted-foreground block truncate text-xs">{item.subtitle}</span>
                                                        )}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {isEmpty && (
                        <div className="text-muted-foreground flex flex-col items-center gap-2 px-4 py-16 text-center text-sm sm:py-8">
                            <Search className="text-muted-foreground/40 size-8" />
                            <span>
                                {t('search.no_results', 'No results for')} &ldquo;{query}&rdquo;
                            </span>
                        </div>
                    )}

                    {query.length < 2 && !loading && (
                        <div className="text-muted-foreground flex flex-col items-center gap-2 px-4 py-16 text-center text-xs sm:py-6">
                            <span>{t('search.hint', 'Type at least 2 characters to search')}</span>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="text-muted-foreground bg-muted/30 hidden shrink-0 items-center gap-4 border-t px-4 py-2 text-[11px] sm:flex">
                    <span className="flex items-center gap-1">
                        <kbd className="bg-muted rounded border px-1 font-mono">↑↓</kbd> {t('search.footer.navigate', 'navigate')}
                    </span>
                    <span className="flex items-center gap-1">
                        <kbd className="bg-muted rounded border px-1 font-mono">↵</kbd> {t('search.footer.open', 'open')}
                    </span>
                    <span className="flex items-center gap-1">
                        <kbd className="bg-muted rounded border px-1 font-mono">Esc</kbd> {t('search.footer.close', 'close')}
                    </span>
                </div>
            </DialogContent>
        </Dialog>
    );
}
